import { LocalTypes } from '../types/CompanyStatistics';
import { merge } from 'lodash';
import Loader, { loadWrap } from './mixins/Loader';
import ApiService from '@jobstairs/axios-api-service';
import { isEmpty, get } from 'lodash';
import tokenService from '@/services/TokenService';
import JobboardService from '@/services/JobboardService';

/**
 * Configuration map defining how to retrieve and transform
 * the top ten data from the api response
 */
const topTenGroups = [
    {
        key: 'standards', // Key to retrieve this data later on
        path: 'jobabo.req.std', // Path to the data in the response object
        remove: ['3'], // Words to remove (tmp cleanup for faulty api)
        replace: [{ match: 'GMP-', substitute: 'GMP' }], // Replace words (tmp cleanup for faulty api)
    },
    {
        key: 'occupations',
        // Custom mapper instead of path as the data structure differs from the standard case
        mapper: (data) =>
            Object.entries(data.suggest.most_common).map(([key, value]) => ({
                text: key,
                count: value.count,
            })),
    },
    { key: 'ikt', path: 'jobabo.req.ikt' },
    { key: 'occupationalFields', path: 'jobabo.req.fld' },
    { key: 'tasks', path: 'jobabo.classification.aufgaben.most_common' },
    {
        key: 'qualifications',
        path: 'jobabo.req.edu',
        remove: ['generell'],
        replace: [
            { match: 'Additional', substitute: 'Weiterbildung' },
            { match: 'Lateral', substitute: 'Quereinstieg' },
        ],
    },
    {
        key: 'keywords',
        path: 'keywords.best_words',
        remove: ['Ihr', 'Ihre', 'Sie'],
    },
];

function buildTopTen(data) {
    return topTenGroups?.reduce((prev, cur) => {
        let words;
        // Map data to top ten data
        if (cur.mapper) {
            // Custom mapper
            words = cur.mapper(data);
        } else {
            // Default mapper: get data array by path
            words = Object.entries(get(data, cur.path) || []).map(
                ([key, value]) => ({
                    text: key,
                    count: value,
                }),
            );
        }

        // TODO: temporary clean up code as the api response is faulty in some cases
        // Filter out words
        if (cur.remove) {
            words = words.filter((word) => !cur.remove.includes(word.text));
        }
        // Replace words
        if (cur.replace) {
            cur?.replace.forEach(({ match, substitute }) => {
                const word = words.find((word) => word.text === match);

                if (word) {
                    word.text = substitute;
                }
            });
        }

        // Sort words based on their occurences so limiting it to ten words eliminates
        // the least important words
        words.sort(({ count: a }, { count: b }) => b - a);
        // Limit result to ten words
        words = words.slice(0, 10);

        return {
            ...prev,
            [cur.key]: words,
        };
    }, {});
}

/**
 * Takes a list of Objects that have an ID field containing a job ad
 * and adds the title of the job-ad to each item.
 * @param {Array} jobAdsList array of job-ad objects
 * @param {JobboardService} service
 * @return {Promise<Array>}
 */
function hydrateJobAds(jobAdsList, service) {
    const promises = jobAdsList.map(async (item) => {
        const info = await service.get(item.ID);
        item.title = info?.PositionTitle;
        return item;
    });
    return Promise.all(promises);
}

function transform(data) {
    data.topTen = buildTopTen(data);
    return data;
}

export default function (config) {
    const apiService = new ApiService({
        baseURL: config.companyStatistics.baseURL,
    });
    const jobboardService = new JobboardService({
        ...config.search,
        request: {
            properties: config.jobAd.properties,
            idProperty: config.jobAd.idProperty,
        },
    });

    return merge(Loader(), {
        state: {
            companyStatistics: {},
            textLength: {
                longestTexts: [],
                shortestTexts: [],
            },
        },
        getters: {},
        actions: {
            async [LocalTypes.actions.LOAD_STATISTICS_ALL]({
                commit,
                dispatch,
            }) {
                await loadWrap(commit, async () => {
                    const headers =
                        tokenService.getAuthenticationHeader().headers;
                    const response = await apiService.get({
                        endpoint: `${config.companyStatistics.endpoint}/overall`,
                        headers,
                    });
                    if (
                        `${response.status}` === '204' ||
                        isEmpty(response.data)
                    ) {
                        throw new Error('204');
                    }

                    await dispatch('hydrateAndSetStatistics', response.data);
                });
            },
            async [LocalTypes.actions.LOAD_STATISTICS](
                { commit, dispatch },
                id,
            ) {
                await loadWrap(commit, async () => {
                    const headers =
                        tokenService.getAuthenticationHeader().headers;
                    let response = await apiService.get({
                        endpoint: `${config.companyStatistics.endpoint}/${id}`,
                        headers,
                    });
                    if (
                        `${response.status}` === '204' ||
                        isEmpty(response.data)
                    ) {
                        throw new Error('204');
                    }

                    await dispatch('hydrateAndSetStatistics', response.data);
                });
            },
            async hydrateAndSetStatistics({ commit }, responseData) {
                const statistics = transform(responseData);
                commit(LocalTypes.mutations.SET_STATISTICS, statistics);

                const textAnalysis = {
                    longestTexts: statistics.readability.longest3_jobads,
                    shortestTexts: statistics.readability.shortest3_jobads,
                };
                const longestTexts = hydrateJobAds(
                    textAnalysis.longestTexts,
                    jobboardService,
                );
                const shortestTexts = hydrateJobAds(
                    textAnalysis.shortestTexts,
                    jobboardService,
                );

                Promise.all([shortestTexts, longestTexts]).then((texts) => {
                    // filter job-ads without a title as that means
                    // they could not be found by the gjb api,
                    // which in turn means that they're most
                    // likely expired
                    textAnalysis.shortestTexts = texts[0].filter(
                        (ad) => ad.title !== undefined,
                    );
                    textAnalysis.longestTexts = texts[1].filter(
                        (ad) => ad.title !== undefined,
                    );
                    commit('setTextLength', textAnalysis);
                });
            },
        },
        mutations: {
            [LocalTypes.mutations.SET_STATISTICS]: (state, statistics) => {
                state.companyStatistics = statistics;
            },
            setTextLength(state, analysisData) {
                state.textLength = analysisData;
            },
        },
    });
}
